import fetch from 'isomorphic-fetch';
import { AuthSiteIdHeaderName, AuthTokenHeaderName } from '../constants.js';
import { isAccountUpdateSiteTagsResponse, isCommerceAccountConfigurationResult, isCommerceAccountLinkTagRequest, isCommerceDiscoverRequest, isCommerceDiscoverResult, isCommerceItemDetailsResult, isCommerceItemsDetailsRequest, isCommerceSearchRequest, isCommerceSearchResult, isSiteListingResponse, } from '../typeGuards/index.js';
import { extractErrorMessage } from '../utility/index.js';
export default class CommerceApiClient {
    constructor(options) {
        this.defaultOptions = {
            apiRoot: '/api/v1/',
        };
        this.endpoints = {
            // Accounts
            linkTags: 'accounts/linkTags',
            siteListing: 'accounts/admin/sites',
            publicSiteListing: 'accounts/sites',
            // Analytics
            updateSiteTags: 'analytics/admin/updateSiteTagList',
            // Products
            discover: 'products/discover',
            search: 'products/search',
            details: 'products/details',
        };
        this.options = { ...this.defaultOptions, ...options };
    }
    async itemDetails(request, authInfo) {
        try {
            if (!isCommerceItemsDetailsRequest(request)) {
                throw new Error('Invalid item details request');
            }
            const response = await fetch(this.getEndpointUri('details'), {
                method: 'POST',
                headers: this.buildRequestHeaders(authInfo, { 'Content-Type': 'application/json' }),
                body: JSON.stringify({ items: request.items }),
            });
            return this.parseResponse(response, 'Item details request', isCommerceItemDetailsResult);
        }
        catch (e) {
            const message = extractErrorMessage(e, 'Unknown error');
            throw new Error('Error encountered while getting details for item: ' + message);
        }
    }
    async searchProducts(search, authInfo) {
        try {
            if (!isCommerceSearchRequest(search)) {
                throw new Error('Invalid search request');
            }
            const response = await fetch(this.getEndpointUri('search') + '?' + this.buildRequestParams(search), { headers: this.buildRequestHeaders(authInfo) });
            return this.parseResponse(response, 'Search request', isCommerceSearchResult);
        }
        catch (e) {
            const message = extractErrorMessage(e, 'Unknown error');
            throw new Error('Error encountered while searching: ' + message);
        }
    }
    async discoverProducts(request, authInfo) {
        try {
            if (!isCommerceDiscoverRequest(request)) {
                throw new Error('Invalid discover request');
            }
            const response = await fetch(this.getEndpointUri('discover') + '?' + this.buildRequestParams(request), { headers: this.buildRequestHeaders(authInfo) });
            return this.parseResponse(response, 'Discover products', isCommerceDiscoverResult);
        }
        catch (e) {
            const message = extractErrorMessage(e, 'Unknown error');
            throw new Error('Error encountered while discovering: ' + message);
        }
    }
    async accountConfiguration(request, authInfo) {
        try {
            if (!isCommerceAccountLinkTagRequest(request)) {
                throw new Error('Invalid commerce account link tag request');
            }
            const params = this.buildRequestParams(request);
            const response = await fetch(this.getEndpointUri('linkTags') + '?' + params.toString(), {
                headers: this.buildRequestHeaders(authInfo),
            });
            return this.parseResponse(response, 'Account configuration', isCommerceAccountConfigurationResult);
        }
        catch (e) {
            const message = extractErrorMessage(e, 'Unknown error');
            throw new Error('Error encountered while attempting to retrieve account configuration: ' + message);
        }
    }
    async siteListing(authInfo) {
        const response = await fetch(this.getEndpointUri('siteListing'), {
            headers: this.buildRequestHeaders(authInfo),
        });
        return this.parseResponse(response, 'Site Listing', isSiteListingResponse);
    }
    async publicSiteListing() {
        const response = await fetch(this.getEndpointUri('publicSiteListing'));
        return this.parseResponse(response, 'Site Listing', isSiteListingResponse);
    }
    async updateSiteTagList(siteAccountId, date, authInfo) {
        if (typeof siteAccountId !== 'string' || typeof date !== 'number') {
            throw new Error('Invalid add site tag list update file request');
        }
        const response = await fetch(this.getEndpointUri('updateSiteTags') + `/${siteAccountId}`, {
            method: 'PUT',
            headers: this.buildRequestHeaders(authInfo, { 'Content-Type': 'application/json' }),
            body: JSON.stringify({ date: date }),
        });
        return this.parseResponse(response, 'Update Site Tag List', isAccountUpdateSiteTagsResponse);
    }
    async parseResponse(response, responseDescriptor, typeGuard) {
        let parsed;
        // Header check needed due to this issue: https://github.com/node-fetch/node-fetch/issues/533
        const responseHeaders = response.headers.get('content-type');
        if (responseHeaders != null && responseHeaders.includes('json')) {
            parsed = await response.json();
        }
        else {
            parsed = await response.text();
        }
        if (!response.ok) {
            throw {
                message: `${responseDescriptor} request returned an error: Status ${response.status}"`,
                status: response.status,
                body: parsed,
            };
        }
        if (!typeGuard(parsed)) {
            throw {
                message: `${responseDescriptor} request returned an invalid result`,
                status: response.status,
                body: parsed,
            };
        }
        return parsed;
    }
    getEndpointUri(endpoint) {
        return this.options.apiRoot + this.endpoints[endpoint];
    }
    buildRequestParams(subject) {
        const params = new URLSearchParams();
        for (const [key, val] of Object.entries(subject)) {
            const value = typeof val === 'string' ? val : val.toString();
            params.set(key, value);
        }
        return params;
    }
    buildRequestHeaders(authHeaders, params) {
        params ?? (params = []);
        const result = new Headers(params);
        // separating the auth and site id so the site id can be supplied in a header separately
        if (authHeaders?.siteId) {
            result.append(AuthSiteIdHeaderName, authHeaders.siteId);
        }
        if (authHeaders?.authKey) {
            result.append(AuthTokenHeaderName, authHeaders.authKey);
        }
        return result;
    }
}
//# sourceMappingURL=client.js.map